import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useMessage } from '../../contexts/MessageContext';
import Loading from '../common/Loading';
import SmartSelect from '../common/SmartSelect';
import { saveContractDownloadBlob, messageFromDownloadError } from '../../utils/contractDownload';
import { copyToClipboard } from '../../utils/clipboard';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const STATUS_COLORS = {
  draft:    'bg-gray-100 text-gray-700',
  sent:     'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  cancelled:'bg-red-100 text-red-700',
  converted:'bg-purple-100 text-purple-800',
};

const emptyLine = () => ({ item_id: '', description: '', quantity: 1, unit_price: 0 });

const CUSTOMER_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const QuoteForm = () => {
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = uuid && /^[0-9a-fA-F-]{36}$/.test(uuid);

  const [quote, setQuote] = useState({
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    customer_id: '',
    status: 'draft',
    notes: '',
    converted_invoice_id: '',
  });
  const [customer, setCustomer] = useState(null);
  const [lines, setLines] = useState([emptyLine()]);
  const [discount, setDiscount] = useState({ type: 'fixed', value: '' });
  const [shareToken, setShareToken] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [contractInfo, setContractInfo] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [generatingContract, setGeneratingContract] = useState(false);
  const [contractCopied, setContractCopied] = useState(false);

  const navigate = useNavigate();
  const { logout } = useAuth();
  const { contractsEnabled, loading: featuresLoading } = useFeatures();
  const { showSuccess, showError, showInfo } = useMessage();

  const token = localStorage.getItem('token');

  const fetchQuote = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/invoice/${uuid}`, {
        headers: { 'x-auth-token': token },
      });
      const data = res.data.data;
      setQuote({
        date: data.date || '',
        due_date: data.due_date || '',
        customer_id: data.customer_id || '',
        status: data.status || 'draft',
        notes: data.notes || '',
        quote_number: data.quote_number || '',
        converted_invoice_id: data.converted_invoice_id || data.ConvertedToInvoice?.uuid || '',
      });
      if (data.Customer) setCustomer(data.Customer);
      if (data.InvoiceLines?.length) {
        setLines(data.InvoiceLines.map(l => ({
          item_id: l.item_id || '',
          description: l.description || '',
          quantity: parseFloat(l.quantity) || 1,
          unit_price: parseFloat(l.unit_price) || 0,
        })));
      }
      const publicTok = data.share_token || data.ConvertedToInvoice?.share_token;
      if (publicTok) setShareToken(publicTok);
      if (data.discount_value > 0) {
        setDiscount({ type: data.discount_type || 'fixed', value: parseFloat(data.discount_value) });
      }
      // Fetch activity log
      try {
        const logRes = await axios.get(`${apiUrl}/invoice/${uuid}/logs`, {
          headers: { 'x-auth-token': token },
        });
        if (logRes.data.success) setLogs(logRes.data.data);
      } catch (_) {}
    } catch (err) {
      console.error('Error fetching quote:', err);
      showError('Failed to load quote');
    } finally {
      setLoading(false);
    }
  }, [uuid, token, showError]);

  const fetchContractSummary = useCallback(async () => {
    if (!contractsEnabled || !isEdit || !uuid) return;
    setContractLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/contracts/by-quote/${uuid}`, {
        headers: { 'x-auth-token': token },
      });
      if (res.data.success) setContractInfo(res.data.data);
      else setContractInfo(null);
    } catch (err) {
      if (err.response?.status !== 403) console.error(err);
      setContractInfo(null);
    } finally {
      setContractLoading(false);
    }
  }, [contractsEnabled, isEdit, uuid, token]);

  useEffect(() => {
    if (!token) {
      logout();
      navigate('/login');
      return;
    }
    if (isEdit) {
      fetchQuote();
      return;
    }
    const cid = searchParams.get('customerId');
    if (cid && CUSTOMER_UUID_RE.test(cid)) {
      (async () => {
        try {
          const res = await axios.get(`${apiUrl}/customers/${cid}`, {
            headers: { 'x-auth-token': token },
          });
          const c = res.data?.data;
          if (c?.uuid) {
            setQuote((prev) => ({ ...prev, customer_id: c.uuid }));
            setCustomer(c);
          }
        } catch (_) {
          /* optional preset */
        }
      })();
    }
  }, [isEdit, token, logout, navigate, fetchQuote, searchParams]);

  useEffect(() => {
    fetchContractSummary();
  }, [fetchContractSummary]);

  // ── Line item helpers ──────────────────────────────────────────────────────

  const updateLine = (idx, field, value) => {
    setLines(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = idx => setLines(prev => prev.filter((_, i) => i !== idx));

  const subtotal = lines.reduce(
    (sum, l) => sum + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0
  );
  const discountAmt = discount.type === 'percent'
    ? subtotal * (parseFloat(discount.value) || 0) / 100
    : (parseFloat(discount.value) || 0);
  const totalAmount = Math.max(0, subtotal - discountAmt);
  const isConverted = quote.status === 'converted';

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isConverted) return;
    if (!quote.customer_id) { showError('Please select a customer'); return; }
    setSaving(true);
    try {
      const payload = { ...quote, invoice_lines: lines, discount_type: discount.type, discount_value: parseFloat(discount.value) || 0 };
      let res;
      if (isEdit) {
        res = await axios.put(`${apiUrl}/invoice/quotes/${uuid}`, payload, {
          headers: { 'x-auth-token': token },
        });
        showSuccess('Quote updated successfully');
        if (res.data.data?.share_token) setShareToken(res.data.data.share_token);
      } else {
        res = await axios.post(`${apiUrl}/invoice/quotes`, payload, {
          headers: { 'x-auth-token': token },
        });
        showSuccess('Quote created successfully');
        const newUuid = res.data.data?.uuid;
        if (newUuid) navigate(`/quotes/${newUuid}`);
        else navigate('/quotes');
        return;
      }
    } catch (err) {
      console.error('Save error:', err);
      showError(err.response?.data?.message || 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  // ── Convert to Invoice ─────────────────────────────────────────────────────

  const handleConvert = async () => {
    if (!window.confirm('Create an order from this quote and generate the invoice? This cannot be undone.')) return;
    setConverting(true);
    try {
      const res = await axios.post(`${apiUrl}/invoice/quotes/${uuid}/convert`, {}, {
        headers: { 'x-auth-token': token },
      });
      const d = res.data?.data || {};
      const orderUuid =
        d.order_uuid || d.order_id || d.order?.uuid || d.Order?.uuid;
      const invoiceUuid =
        d.invoice_uuid || d.invoice?.uuid || d.Invoice?.uuid || d.uuid;
      showSuccess(
        orderUuid
          ? 'Order and invoice created'
          : 'Quote converted to invoice (no inventory lines, so no order was created)'
      );
      if (orderUuid) {
        navigate(`/orders/${orderUuid}`);
      } else if (invoiceUuid) {
        navigate(`/invoice/${invoiceUuid}`);
      } else {
        navigate('/invoice');
      }
    } catch (err) {
      console.error('Convert error:', err);
      showError(err.response?.data?.message || 'Failed to convert quote');
    } finally {
      setConverting(false);
    }
  };

  // ── Share link ─────────────────────────────────────────────────────────────

  const shareUrl = shareToken
    ? `${window.location.origin}/quote/view/${shareToken}`
    : null;

  const copyShareLink = () => {
    if (!shareUrl) return;
    copyToClipboard(shareUrl, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      showInfo('Share link copied to clipboard');
    });
  };

  const contractShareUrl =
    contractInfo?.share_token
      ? `${window.location.origin}/contract/view/${contractInfo.share_token}`
      : null;

  const copyContractLink = () => {
    if (!contractShareUrl) return;
    copyToClipboard(contractShareUrl, () => {
      setContractCopied(true);
      setTimeout(() => setContractCopied(false), 2500);
      showInfo('Contract link copied');
    });
  };

  const handleGenerateContract = async () => {
    if (!uuid || isConverted) return;
    setGeneratingContract(true);
    try {
      const res = await axios.post(`${apiUrl}/contracts/from-quote/${uuid}`, {}, {
        headers: { 'x-auth-token': token },
      });
      if (res.data.success) {
        showSuccess('Contract generated. Share the contract link with your customer.');
        await fetchContractSummary();
      } else {
        showError(res.data.message || 'Failed to generate contract');
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to generate contract');
    } finally {
      setGeneratingContract(false);
    }
  };

  const downloadContractPdf = async () => {
    if (!contractInfo?.uuid || !contractInfo.pdf_ready) return;
    try {
      const res = await axios.get(`${apiUrl}/contracts/${contractInfo.uuid}/pdf`, {
        headers: { 'x-auth-token': token },
        responseType: 'blob',
      });
      saveContractDownloadBlob(res, `contract-${contractInfo.uuid}`);
    } catch (err) {
      const msg = await messageFromDownloadError(err, 'Could not download contract');
      showError(msg);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="p-4 md:p-6 my-2 md:my-6 bg-white shadow-lg rounded-lg">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-gray-800">
            {isEdit ? `Quote ${quote.quote_number || ''}` : 'New Quote'}
          </h2>
          {isEdit && (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[quote.status] || 'bg-gray-100 text-gray-700'}`}>
              {quote.status}
            </span>
          )}
        </div>

        {/* Share link */}
        {isEdit && shareUrl && (
          <button
            type="button"
            onClick={copyShareLink}
            className="flex items-center gap-1 px-4 py-2 border border-blue-300 text-blue-600 rounded-md text-sm hover:bg-blue-50 transition-colors"
          >
            {copied ? (
              <>&#10003; Copied!</>
            ) : (
              <>&#128279; Copy Customer Link</>
            )}
          </button>
        )}
      </div>

      {isEdit && isConverted && quote.converted_invoice_id && (
        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-md text-sm text-purple-900">
          This quote is <strong>converted</strong>. It stays in your quotes list for reference. The customer&apos;s original link still opens the invoice.{' '}
          <button
            type="button"
            className="underline font-semibold text-purple-800 hover:text-purple-950"
            onClick={() => navigate(`/invoice/${quote.converted_invoice_id}`)}
          >
            Open invoice
          </button>
        </div>
      )}

      {isEdit && contractsEnabled && !featuresLoading && (
        <div className="mb-4 p-4 border border-amber-200 bg-amber-50 rounded-md">
          <h3 className="text-sm font-semibold text-amber-900 mb-2">Contract (add-on)</h3>
          <p className="text-xs text-amber-800 mb-3">
            Generate an HTML contract and PDF from this quote. Send the contract link so the customer can review and accept online.
          </p>
          {contractLoading ? (
            <p className="text-sm text-amber-700">Loading contract status…</p>
          ) : (
            <>
              {contractInfo && (
                <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
                  <span className="px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-900 capitalize">
                    {contractInfo.status}
                  </span>
                  <span className="text-amber-800">Version {contractInfo.version}</span>
                  {contractInfo.pdf_ready && (
                    <button
                      type="button"
                      onClick={downloadContractPdf}
                      className="text-amber-900 underline text-sm"
                    >
                      Download contract
                    </button>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={generatingContract || isConverted}
                  onClick={handleGenerateContract}
                  className="px-4 py-2 bg-amber-700 text-white rounded-md text-sm hover:bg-amber-800 disabled:opacity-50"
                >
                  {generatingContract ? 'Generating…' : contractInfo ? 'Regenerate contract' : 'Generate contract'}
                </button>
                {contractShareUrl && (
                  <button
                    type="button"
                    onClick={copyContractLink}
                    className="px-4 py-2 border border-amber-600 text-amber-900 rounded-md text-sm hover:bg-amber-100"
                  >
                    {contractCopied ? 'Copied!' : 'Copy contract link'}
                  </button>
                )}
              </div>
              {isConverted && (
                <p className="text-xs text-amber-700 mt-2">Contracts cannot be regenerated after the quote is converted.</p>
              )}
            </>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className={isConverted ? 'pointer-events-none opacity-70' : ''}>
        {/* ── Quote info ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quote Date <span className="text-red-500">*</span></label>
            <input
              type="date"
              name="date"
              value={quote.date}
              onChange={e => setQuote(p => ({ ...p, date: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
            <input
              type="date"
              name="due_date"
              value={quote.due_date}
              onChange={e => setQuote(p => ({ ...p, due_date: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer <span className="text-red-500">*</span></label>
            <SmartSelect
              value={quote.customer_id}
              onChange={newId => setQuote(p => ({ ...p, customer_id: newId }))}
              initialRecord={customer}
              model="customers"
              placeholder="Search or create customer..."
              creatable={true}
              additionalCreateFields={{}}
            />
          </div>

          {isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              {isConverted ? (
                <p className="w-full p-2 border border-purple-200 rounded-md bg-purple-50 text-purple-900 text-sm font-medium capitalize">
                  Converted (read-only)
                </p>
              ) : (
                <select
                  value={quote.status}
                  onChange={e => setQuote(p => ({ ...p, status: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="accepted">Accepted</option>
                  <option value="declined">Declined</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              )}
            </div>
          )}
        </div>

        {/* ── Line items ──────────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Line Items</h3>
            {!isConverted && (
            <button
              type="button"
              onClick={addLine}
              className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              + Add Line
            </button>
            )}
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded-md">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="text-left py-2 px-3">Item / Inventory</th>
                  <th className="text-left py-2 px-3 w-1/3">Description</th>
                  <th className="text-right py-2 px-3 w-20">Qty</th>
                  <th className="text-right py-2 px-3 w-28">Unit Price</th>
                  <th className="text-right py-2 px-3 w-28">Total</th>
                  <th className="py-2 px-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((line, idx) => (
                  <tr key={idx} className="align-top">
                    <td className="py-2 px-3">
                      <SmartSelect
                        value={line.item_id}
                        onChange={() => {}}
                        onSelect={item => {
                          setLines(prev => prev.map((l, i) =>
                            i === idx
                              ? {
                                  ...l,
                                  item_id: item?.uuid || '',
                                  description: item?.name || l.description,
                                  unit_price: item?.price != null ? parseFloat(item.price) : l.unit_price,
                                }
                              : l
                          ));
                        }}
                        model="inventory"
                        labelField="name"
                        placeholder="Search inventory..."
                        creatable={false}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <input
                        type="text"
                        value={line.description}
                        onChange={e => updateLine(idx, 'description', e.target.value)}
                        placeholder="Description"
                        className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-400"
                      />
                    </td>
                    <td className="py-2 px-3">
                      <input
                        type="number"
                        value={line.quantity}
                        min="0"
                        step="0.01"
                        onChange={e => updateLine(idx, 'quantity', e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded text-sm text-right focus:ring-1 focus:ring-blue-400"
                      />
                    </td>
                    <td className="py-2 px-3">
                      <input
                        type="number"
                        value={line.unit_price}
                        min="0"
                        step="0.01"
                        onChange={e => updateLine(idx, 'unit_price', e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded text-sm text-right focus:ring-1 focus:ring-blue-400"
                      />
                    </td>
                    <td className="py-2 px-3 text-right font-medium text-gray-700">
                      ${((parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0)).toFixed(2)}
                    </td>
                    <td className="py-2 px-2 text-center">
                      {!isConverted && lines.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLine(idx)}
                          className="text-red-400 hover:text-red-600 text-lg leading-none"
                          title="Remove line"
                        >
                          &times;
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 text-sm">
                {/* Subtotal row */}
                <tr>
                  <td colSpan={4} className="text-right py-1 px-3 text-gray-500">Subtotal</td>
                  <td className="py-1 px-3 text-right text-gray-700">${subtotal.toFixed(2)}</td>
                  <td />
                </tr>
                {/* Discount row */}
                <tr>
                  <td colSpan={3} className="text-right py-1 px-3 text-gray-500">Discount</td>
                  <td className="py-1 px-3">
                    <div className="flex items-center justify-end gap-1">
                      {/* Type toggle */}
                      <div className="flex border border-gray-300 rounded overflow-hidden text-xs">
                        <button
                          type="button"
                          onClick={() => setDiscount(d => ({ ...d, type: 'fixed' }))}
                          className={`px-2 py-1 ${discount.type === 'fixed' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        >$</button>
                        <button
                          type="button"
                          onClick={() => setDiscount(d => ({ ...d, type: 'percent' }))}
                          className={`px-2 py-1 ${discount.type === 'percent' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        >%</button>
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        max={discount.type === 'percent' ? 100 : undefined}
                        value={discount.value}
                        onChange={e => setDiscount(d => ({ ...d, value: e.target.value }))}
                        placeholder="0"
                        className="w-20 p-1 border border-gray-300 rounded text-right focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </td>
                  <td className="py-1 px-3 text-right text-red-600">
                    {discountAmt > 0 ? `- $${discountAmt.toFixed(2)}` : '—'}
                  </td>
                  <td />
                </tr>
                {/* Total row */}
                <tr className="border-t border-gray-300">
                  <td colSpan={4} className="text-right py-2 px-3 font-semibold text-gray-700">Total</td>
                  <td className="py-2 px-3 text-right font-bold text-gray-900">${totalAmount.toFixed(2)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* ── Notes ───────────────────────────────────────────────────────── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes for Customer</label>
          <textarea
            value={quote.notes}
            onChange={e => setQuote(p => ({ ...p, notes: e.target.value }))}
            rows={3}
            placeholder="Terms, conditions, or any notes visible to the customer..."
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
        </div>

        {/* ── Actions ─────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving || isConverted}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving...' : isEdit ? 'Update Quote' : 'Save Quote'}
          </button>

          {isEdit && !isConverted && quote.status !== 'declined' && quote.status !== 'cancelled' && (
            <button
              type="button"
              disabled={converting}
              onClick={handleConvert}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 font-medium"
            >
              {converting ? 'Converting...' : 'Create order & invoice'}
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate('/quotes')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
        </div>

        {/* ── Share URL display ────────────────────────────────────────────── */}
        {isEdit && shareUrl && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md pointer-events-auto">
            <p className="text-xs text-blue-700 font-medium mb-1">Customer Share Link</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 text-xs p-2 border border-blue-300 rounded bg-white text-gray-700 select-all"
                onClick={e => e.target.select()}
              />
              <button
                type="button"
                onClick={copyShareLink}
                className="text-xs px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-xs text-blue-500 mt-1">
              {isConverted
                ? 'Same link as before: it now opens the invoice for your customer.'
                : 'Send this link to your customer. They can view and accept/decline the quote without logging in.'}
            </p>
          </div>
        )}
      </form>

      {/* ── Activity Log ──────────────────────────────────────────────────── */}
      {isEdit && (
        <div className="mt-8">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Customer Activity</h3>
          {logs.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No activity yet. Share the link with your customer to start tracking.</p>
          ) : (
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2">Date &amp; Time</th>
                    <th className="text-left px-4 py-2">Event</th>
                    <th className="text-left px-4 py-2">IP Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map(log => (
                    <tr key={log.uuid} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          log.event === 'viewed'   ? 'bg-blue-100 text-blue-700' :
                          log.event === 'accepted' ? 'bg-green-100 text-green-700' :
                          log.event === 'converted_from_quote' ? 'bg-purple-100 text-purple-800' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {log.event === 'converted_from_quote'
                            ? 'Converted to invoice'
                            : log.event.charAt(0).toUpperCase() + log.event.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-500 font-mono text-xs">{log.ip_address || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuoteForm;
